import React, { useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  Space,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import type {
  OrderSettlementCommandResultDto,
  OrderSettlementReadinessDto,
} from '../../../dto/orderSettlement';
import { formatCurrencyLocalized } from '../../../utils/currency';
import { resolveOrderSettlementCommand } from '../api/orderSettlementApi';
import { runOrderSettlementCommandMutation } from '../orderSettlementCommandMutation';

interface Props {
  orderId: string;
  readiness: OrderSettlementReadinessDto | null;
  canViewFinance: boolean;
  onRefetch: () => Promise<void> | void;
  onResult: (result: OrderSettlementCommandResultDto, source: 'ADMIN_RESOLUTION') => void;
}

type ResolutionAction = 'RETRY' | 'SUPERSEDE';

interface ResolutionValues {
  reason: string;
}

/**
 * ADMIN-only resolution of a frozen command. The component is deliberately
 * absent from operational surfaces: it can expose a financial preview and the
 * internal reason without relying on frontend gating as the security boundary.
 */
export const OrderSettlementCommandResolutionPanel: React.FC<Props> = ({
  orderId,
  readiness,
  canViewFinance,
  onRefetch,
  onResult,
}) => {
  const { t, i18n } = useTranslation(['orders', 'common']);
  const [form] = Form.useForm<ResolutionValues>();
  const [selectedAction, setSelectedAction] = useState<ResolutionAction | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const command = readiness?.unresolvedCommand;

  if (!canViewFinance || !command) return null;

  const resolve = async (values: ResolutionValues) => {
    if (submitting || !selectedAction) return;

    const reason = values.reason.trim();
    if (!reason) return;

    const financialPreviewVersion = readiness?.financialPreviewVersion;
    if (selectedAction === 'RETRY' && !financialPreviewVersion) {
      setNotice(t('orders:orderDetail.settlementCommandResolveError'));
      return;
    }

    setSubmitting(true);
    setNotice(null);
    try {
      const result = await runOrderSettlementCommandMutation(
        () => resolveOrderSettlementCommand(
          orderId,
          command.id,
          selectedAction === 'RETRY'
            ? { action: 'RETRY', reason, financialPreviewVersion: financialPreviewVersion! }
            : { action: 'SUPERSEDE', reason },
        ),
        onRefetch,
      );
      if (!result.state) {
        throw new Error('Invalid settlement command resolution response');
      }
      onResult(result, 'ADMIN_RESOLUTION');
      if (result.state === 'ADMIN_REQUIRED') {
        setNotice(t('orders:orderDetail.settlementCommandStillAdminRequired'));
      }
    } catch (error: any) {
      setNotice(error?.response?.status === 409
        ? t('orders:orderDetail.settlementCommandStale')
        : t('orders:orderDetail.settlementCommandResolveError'));
    } finally {
      setSubmitting(false);
    }
  };

  const financialPreview = readiness?.financialPreview;
  const financialBlockedReasons = readiness?.financialBlockedReasons || [];
  const actionLabel = selectedAction === 'RETRY'
    ? t('orders:orderDetail.settlementCommandRetry')
    : t('orders:orderDetail.settlementCommandSupersede');
  const reasonLabel = selectedAction === 'RETRY'
    ? t('orders:orderDetail.settlementCommandRetryReason')
    : t('orders:orderDetail.settlementCommandSupersedeReason');

  return (
    <Alert
      type="warning"
      showIcon
      message={t('orders:orderDetail.settlementCommandAdminRequired')}
      description={(
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Typography.Paragraph style={{ margin: 0 }}>
            {t('orders:orderDetail.settlementCommandAdminPanelDescription')}
          </Typography.Paragraph>
          {command.recognitionMonth && (
            <Typography.Text>{t('orders:orderDetail.resultChosenMonth', { month: command.recognitionMonth })}</Typography.Text>
          )}
          <div>
            <Typography.Text type="secondary">
              {t('orders:orderDetail.settlementCommandInternalReason')}
            </Typography.Text>
            <br />
            <Typography.Text code>{command.internalReasonCode || '—'}</Typography.Text>
          </div>
          {financialPreview && (
            <Typography.Text>
              {t('orders:orderDetail.settlementAutomaticPreview', {
                revenue: formatCurrencyLocalized(financialPreview.revenue, 'PLN', i18n.language),
                totalCost: formatCurrencyLocalized(financialPreview.totalCost, 'PLN', i18n.language),
                margin: formatCurrencyLocalized(financialPreview.margin, 'PLN', i18n.language),
              })}
            </Typography.Text>
          )}
          {financialBlockedReasons.length > 0 && (
            <div>
              <Typography.Text type="secondary">
                {t('orders:orderDetail.settlementCommandFinancialReasons')}
              </Typography.Text>
              {financialBlockedReasons.map((reason) => (
                <div key={`${reason.code}-${reason.message}`}>
                  <Typography.Text>{reason.message || reason.code}</Typography.Text>
                </div>
              ))}
            </div>
          )}
          {notice && <Alert type="info" showIcon message={notice} />}
          {!selectedAction ? (
            <Space wrap>
              <Button type="primary" loading={submitting} onClick={() => setSelectedAction('RETRY')}>
                {t('orders:orderDetail.settlementCommandRetry')}
              </Button>
              <Button disabled={submitting} onClick={() => setSelectedAction('SUPERSEDE')}>
                {t('orders:orderDetail.settlementCommandSupersede')}
              </Button>
            </Space>
          ) : (
            <Form
              form={form}
              layout="vertical"
              onFinish={(values) => void resolve(values)}
            >
              <Form.Item
                name="reason"
                label={reasonLabel}
                rules={[{
                  required: true,
                  whitespace: true,
                  message: t('orders:orderDetail.settlementCommandResolutionReasonRequired'),
                }]}
              >
                <Input.TextArea maxLength={1000} rows={3} showCount />
              </Form.Item>
              <Space wrap>
                <Button
                  type="primary"
                  danger={selectedAction === 'SUPERSEDE'}
                  htmlType="submit"
                  loading={submitting}
                >
                  {t('orders:orderDetail.settlementCommandConfirmResolution', { action: actionLabel })}
                </Button>
                <Button
                  disabled={submitting}
                  onClick={() => {
                    form.resetFields();
                    setSelectedAction(null);
                  }}
                >
                  {t('common:cancel')}
                </Button>
              </Space>
            </Form>
          )}
        </Space>
      )}
    />
  );
};
